import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'ar' | 'en';
type Direction = 'rtl' | 'ltr';

interface LanguageContextType {
    language: Language;
    direction: Direction;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const translations = {
    ar: {
        // Sidebar
        'dashboard': 'لوحة التحكم',
        'orders': 'الطلبات',
        'products': 'المنتجات',
        'customers': 'العملاء',
        'analytics': 'التحليلات',
        'marketing': 'التسويق',
        'content': 'المحتوى',
        'online_store': 'المتجر الإلكتروني',
        'settings': 'الإعدادات',
        'apps': 'التطبيقات',

        // Header
        'search_placeholder': 'بحث سريع...',
        'notifications': 'الإشعارات',
        'profile': 'الملف الشخصي',
        'view_store': 'عرض المتجر',

        // Dashboard
        'good_evening': 'مساء الخير',
        'good_morning': 'صباح الخير',
        'store_overview': 'نظرة عامة على أداء متجرك',
        'live_view': 'العرض المباشر',
        'actions_required': 'إجراءات مطلوبة',
        'total_sales': 'إجمالي المبيعات',
        'sessions': 'الجلسات',
        'conversion_rate': 'معدل التحويل',
        'aov': 'متوسط الطلب',
        'compare_period': 'مقارنة بالفترة السابقة',
    },
    en: {
        // Sidebar
        'dashboard': 'Dashboard',
        'orders': 'Orders',
        'products': 'Products',
        'customers': 'Customers',
        'analytics': 'Analytics',
        'marketing': 'Marketing',
        'content': 'Content',
        'online_store': 'Online Store',
        'settings': 'Settings',
        'apps': 'Apps',

        // Header
        'search_placeholder': 'Quick search...',
        'notifications': 'Notifications',
        'profile': 'Profile',
        'view_store': 'View Store',

        // Dashboard
        'good_evening': 'Good Evening',
        'good_morning': 'Good Morning',
        'store_overview': 'Overview of your store performance',
        'live_view': 'Live View',
        'actions_required': 'Actions Required',
        'total_sales': 'Total Sales',
        'sessions': 'Sessions',
        'conversion_rate': 'Conversion Rate',
        'aov': 'Avg. Order Value',
        'compare_period': 'vs previous period',
    }
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguage] = useState<Language>('ar');
    const [direction, setDirection] = useState<Direction>('rtl');

    useEffect(() => {
        const savedLang = localStorage.getItem('language') as Language;
        if (savedLang) {
            setLanguage(savedLang);
            setDirection(savedLang === 'ar' ? 'rtl' : 'ltr');
        }
        document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = savedLang || 'ar';
    }, []);

    const handleSetLanguage = (lang: Language) => {
        setLanguage(lang);
        const newDir = lang === 'ar' ? 'rtl' : 'ltr';
        setDirection(newDir);
        localStorage.setItem('language', lang);
        document.documentElement.dir = newDir;
        document.documentElement.lang = lang;
    };

    const t = (key: string) => {
        return translations[language][key as keyof typeof translations['ar']] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, direction, setLanguage: handleSetLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        console.warn('useLanguage used outside of LanguageProvider, falling back to default');
        return {
            language: 'ar',
            direction: 'rtl',
            setLanguage: () => { },
            t: (key: string) => key
        } as LanguageContextType;
    }
    return context;
};
